import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, Search, BookOpen, Play, Lock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import katex from 'katex';
import DOMPurify from 'dompurify';
import 'highlight.js/styles/atom-one-dark.css';
import 'katex/dist/katex.min.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InternalApiHub from '../components/InternalApiHub';
import { docsSections, docsContent } from '../mock';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { usePlayground } from '../contexts/PlaygroundContext';
import { toast } from 'sonner';

// Función para renderizar título con LaTeX (usa title_latex del JSON si existe)
const renderLatexTitle = (title, titleLatex) => {
  if (!titleLatex) return null;
  
  try {
    // Manejar títulos con <br/> (2 líneas)
    if (titleLatex.includes('<br/>')) {
      const parts = titleLatex.split('<br/>');
      const renderedParts = parts.map((part, index) => {
        let latex = part.trim();
        if (latex.startsWith('$') && latex.endsWith('$')) {
          latex = latex.slice(1, -1);
        }
        const rendered = katex.renderToString(latex, { throwOnError: false, displayMode: false });
        // Segunda línea con fuente más pequeña
        if (index === 1) {
          return `<span style="font-size: 0.8em; opacity: 0.9;">${rendered}</span>`;
        }
        return rendered;
      });
      return renderedParts.join('<br/>');
    }
    
    // Quitar los delimitadores $...$ si existen
    let latex = titleLatex.trim();
    if (latex.startsWith('$') && latex.endsWith('$')) {
      latex = latex.slice(1, -1);
    }
    return katex.renderToString(latex, { throwOnError: false, displayMode: false });
  } catch (e) {
    return null;
  }
};

// Componente personalizado para bloques de código con estilo terminal
const CodeBlock = ({ children, className, onSendToPlayground, restricted = false }) => {
  const language = className?.replace('language-', '').replace('hljs ', '') || '';
  const langLower = language.toLowerCase();
  
  // Comandos del S.O. y salidas de terminal - estilo simple sin botones
  const isSystemCommand = ['cmd', 'bash', 'shell', 'sh', 'terminal', 'powershell', ''].includes(langLower);
  
  // Archivos de configuración (json, xml, etc.) - estilo neutro sin botones
  const isConfigFile = ['json', 'xml', 'yaml', 'yml', 'ini', 'toml', 'config'].includes(langLower);
  
  // Solo código BLOX tiene la caja bonita con botones
  const isBloxCode = langLower === 'blox';

  // Extraer el texto del código (puede ser string o array de elementos)
  const getCodeText = (node) => {
    if (typeof node === 'string') return node;
    if (Array.isArray(node)) return node.map(getCodeText).join('');
    if (node?.props?.children) return getCodeText(node.props.children);
    return '';
  };

  const codeText = getCodeText(children);
  
  if (isBloxCode) {
    // Código BLOX - estilo con botones de terminal (azul)
    return (
      <div className="my-4 rounded-xl border border-cyan-900/40 overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-4 py-2 bg-cyan-950/50 border-b border-cyan-900/40">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <span className="text-xs text-cyan-500 ml-2">ejemplo.blox</span>
          </div>
          {restricted ? (
            <span
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-amber-300 bg-amber-950/40 border border-amber-700/40 rounded-md cursor-not-allowed select-none"
              title="Ejecución en Playground no disponible para esta API"
              data-testid="send-to-playground-restricted"
            >
              <Lock className="w-3 h-3" />
              Solo lectura
            </span>
          ) : onSendToPlayground && (
            <button
              onClick={() => onSendToPlayground(codeText)}
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-cyan-400 bg-cyan-950/80 hover:bg-cyan-900/80 border border-cyan-700/50 rounded-md transition-colors"
              data-testid="send-to-playground-btn"
            >
              <Play className="w-3 h-3" />
              Probar en Playground
            </button>
          )}
        </div>
        <div className="p-4 overflow-x-auto bg-slate-950">
          <pre className="text-slate-300 font-mono text-sm leading-relaxed !bg-transparent !p-0 !m-0">
            <code className="!bg-transparent">{children}</code>
          </pre>
        </div>
        {restricted && (
          <div
            className="px-4 py-2 text-xs text-amber-300 bg-amber-950/30 border-t border-amber-900/40"
            data-testid="restricted-banner"
          >
            <strong>Restringido:</strong> este ejemplo no puede ejecutarse en el Playground web porque la API requerida no está disponible en el sandbox.
          </div>
        )}
      </div>
    );
  }
  
  if (isConfigFile) {
    return (
      <div className="my-4 bg-slate-900 rounded-lg border border-slate-700 overflow-hidden">
        <div className="px-4 py-2 bg-slate-800 border-b border-slate-700">
          <span className="text-xs text-slate-400 font-mono">{language}</span>
        </div>
        <div className="p-4 overflow-x-auto">
          <pre className="text-slate-300 font-mono text-sm leading-relaxed !bg-transparent !p-0 !m-0">
            <code className="!bg-transparent">{children}</code>
          </pre>
        </div>
      </div>
    );
  }
  
  // Terminal/comandos/salidas - estilo simple sin botones
  return (
    <div className="my-4 bg-slate-900 rounded-lg border border-slate-700 overflow-hidden">
      <div className="p-4 overflow-x-auto">
        <pre className="text-slate-300 font-mono text-sm leading-relaxed !bg-transparent !p-0 !m-0">
          <code className="!bg-transparent">{children}</code>
        </pre>
      </div>
    </div>
  );
};

const DocsPage = () => {
  const { category, topic } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const { sendToPlayground } = usePlayground();

  // Secciones que son colapsables (APIs y grupos internos). Las secciones
  // estáticas introductorias se dejan siempre abiertas por ser cortas.
  const isCollapsibleSection = (section) =>
    section.id.startsWith('api-') || section.id.startsWith('internas-');

  // Estado: set de ids de secciones colapsables abiertas.
  // Inicializamos vacío; el useEffect de abajo abre automáticamente la
  // sección que contiene la ruta activa.
  const [openSections, setOpenSections] = useState(() => new Set());

  // Función para enviar código al playground
  const handleSendToPlayground = (code) => {
    sendToPlayground(code);
    toast.success('Código copiado al Playground', {
      description: 'Redirigiendo al Playground...',
      duration: 2000,
    });
    // Navegar al playground (página principal con hash)
    setTimeout(() => {
      navigate('/#playground');
      // Scroll al playground después de navegar
      setTimeout(() => {
        const playgroundSection = document.getElementById('playground');
        if (playgroundSection) {
          playgroundSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }, 500);
  };

  // Determinar el contenido actual - manejar rutas de API y funciones internas
  let currentPath = topic || 'what-is-blox';
  
  // Si es una ruta de API, construir el ID completo
  if (category && category.startsWith('api-')) {
    const apiName = category.replace('api-', '');
    currentPath = `${apiName}-${topic}`;
  }
  
  // Si es una ruta de funciones internas, construir el ID completo
  if (category && category.startsWith('internas-')) {
    currentPath = `int-${topic}`;
  }
  
  const currentDoc = docsContent[currentPath] || docsContent['what-is-blox'];

  // Detectar si el Markdown ya arranca con un H1 propio (para evitar duplicar el título).
  // NO aplica cuando la página renderiza un componente especial (ej. el hub), porque en
  // ese caso el Markdown no se muestra y ocultar el título dejaría la página sin encabezado.
  const isSpecialPage = currentPath === 'internal-api';
  const contentStartsWithH1 = React.useMemo(() => {
    if (isSpecialPage) return false;
    const c = currentDoc.content || '';
    return /^\s*#\s+\S/.test(c);
  }, [currentDoc.content, isSpecialPage]);

  // Auto-abrir la sección colapsable que contiene el item activo.
  // Solo depende de currentPath: docsSections es una constante importada del módulo.
  React.useEffect(() => {
    const activeSection = docsSections.find(s =>
      s.items.some(it => it.id === currentPath)
    );
    if (activeSection && isCollapsibleSection(activeSection)) {
      setOpenSections(prev => {
        if (prev.has(activeSection.id)) return prev;
        const next = new Set(prev);
        next.add(activeSection.id);
        return next;
      });
    }
    // docsSections es una constante importada del módulo; no cambia.
  }, [currentPath]);

  const toggleSection = (sectionId) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  // Buscar en títulos y contenido
  React.useEffect(() => {
    if (searchQuery.length >= 2) {
      const query = searchQuery.toLowerCase();
      const results = [];
      
      // Buscar en todas las secciones y contenidos
      docsSections.forEach(section => {
        section.items.forEach(item => {
          const content = docsContent[item.id];
          if (content) {
            const titleMatch = item.title.toLowerCase().includes(query);
            const contentMatch = content.content?.toLowerCase().includes(query);
            const codeMatch = content.code?.toLowerCase().includes(query);
            
            if (titleMatch || contentMatch || codeMatch) {
              results.push({
                ...item,
                sectionTitle: section.title,
                matchType: titleMatch ? 'title' : (contentMatch ? 'content' : 'code')
              });
            }
          }
        });
      });
      
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
    // docsSections y docsContent son constantes importadas del módulo; no cambian.
  }, [searchQuery]);

  // Filtrar secciones según búsqueda (para el menú)
  const filteredSections = searchQuery.length >= 2
    ? docsSections.map(section => ({
        ...section,
        items: section.items.filter(item => {
          const content = docsContent[item.id];
          const query = searchQuery.toLowerCase();
          return item.title.toLowerCase().includes(query) ||
                 content?.content?.toLowerCase().includes(query) ||
                 content?.code?.toLowerCase().includes(query);
        })
      })).filter(section => section.items.length > 0)
    : docsSections;

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <Header />
      
      <div className="flex-1 pt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24 bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6">
                <div className="mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      type="text"
                      placeholder="Buscar funciones, temas..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-white"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  
                  {/* Resultados de búsqueda */}
                  {searchQuery.length >= 2 && (
                    <div className="mt-3 text-sm text-slate-400">
                      {searchResults.length > 0 ? (
                        <span>{searchResults.length} resultado(s) encontrado(s)</span>
                      ) : (
                        <span>No se encontraron resultados para "{searchQuery}"</span>
                      )}
                    </div>
                  )}
                </div>

                <ScrollArea className="h-[calc(100vh-300px)]">
                  <nav className="space-y-6">
                    {filteredSections.map((section) => {
                      const collapsible = isCollapsibleSection(section);
                      // En modo búsqueda, forzar abiertas todas las secciones
                      // para que los resultados sean visibles.
                      const isOpen =
                        searchQuery.length >= 2 ||
                        !collapsible ||
                        openSections.has(section.id);

                      return (
                        <div key={section.id}>
                          {collapsible ? (
                            <button
                              type="button"
                              onClick={() => toggleSection(section.id)}
                              data-testid={`sidebar-toggle-${section.id}`}
                              className="w-full flex items-center justify-between text-left text-sm font-semibold text-slate-400 hover:text-white uppercase tracking-wider mb-3 transition-colors"
                            >
                              <span>{section.title}</span>
                              {isOpen ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </button>
                          ) : (
                            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                              {section.title}
                            </h3>
                          )}
                          {isOpen && (
                            <ul className="space-y-2">
                              {section.items.map((item) => (
                                <li key={item.id}>
                                  <Link
                                    to={item.path}
                                    className={`block text-sm py-2 px-3 rounded-lg transition-colors ${
                                      currentPath === item.id
                                        ? 'bg-cyan-500/10 text-cyan-400 font-medium'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                                    }`}
                                  >
                                    {item.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </nav>
                </ScrollArea>
              </div>
            </aside>

            {/* Main Content */}
            <main className="lg:col-span-3">
              <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 lg:p-12">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-slate-400 mb-8">
                  <Link to="/docs" className="hover:text-cyan-400 transition-colors">
                    Documentación
                  </Link>
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-white">{currentDoc.title}</span>
                </div>

                {/* Title (se oculta si el Markdown ya tiene un H1 propio y no hay LaTeX especial) */}
                {renderLatexTitle(currentDoc.title, currentDoc.titleLatex) ? (
                  <h1 
                    className="text-3xl font-bold text-white mb-6 leading-normal"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        renderLatexTitle(currentDoc.title, currentDoc.titleLatex),
                        { ADD_TAGS: ['math', 'mi', 'mo', 'mn', 'mrow', 'mtext', 'annotation', 'semantics', 'mspace', 'mstyle', 'msup', 'msub', 'msubsup', 'mfrac', 'msqrt', 'mroot'], ADD_ATTR: ['mathvariant', 'xmlns', 'encoding'] }
                      )
                    }}
                  />
                ) : !contentStartsWithH1 ? (
                  <h1 className="text-3xl font-bold text-white mb-6 font-mono">
                    {currentDoc.title}
                  </h1>
                ) : null}

                {/* Content */}
                {currentPath === 'internal-api' ? (
                  <InternalApiHub />
                ) : (
                <div className="prose prose-invert prose-slate max-w-none prose-headings:text-white prose-h1:text-3xl prose-h2:text-2xl prose-h2:text-cyan-400 prose-h3:text-xl prose-h3:text-slate-200 prose-p:text-slate-300 prose-li:text-slate-300 prose-strong:text-white prose-code:before:content-none prose-code:after:content-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeHighlight, rehypeRaw, rehypeKatex]}
                    components={{
                      pre: ({ children }) => <>{children}</>,
                      code: ({ node, className, children, ...props }) => {
                        const isBlock = className || (typeof children === 'string' && children.includes('\n'));
                        if (!isBlock) {
                          return <code className="text-cyan-400 bg-cyan-950/50 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>;
                        }
                        return <CodeBlock className={className} onSendToPlayground={handleSendToPlayground} restricted={currentDoc.playgroundAccess === 'restricted'}>{children}</CodeBlock>;
                      }
                    }}
                  >
                    {currentDoc.content}
                  </ReactMarkdown>
                </div>
                )}

                {/* Code Example */}
                {currentDoc.code && (
                  <div className="mt-8">
                    <h3 className="text-xl font-semibold text-white mb-4">Ejemplo de Código</h3>
                    <div className="bg-slate-950 rounded-xl border border-slate-800 overflow-hidden">
                      <div className="flex items-center gap-2 px-4 py-3 bg-slate-900 border-b border-slate-800">
                        <div className="flex gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <span className="text-xs text-slate-500 ml-2">ejemplo.blox</span>
                      </div>
                      <div className="p-6 overflow-x-auto">
                        <pre className="text-slate-300 font-mono text-sm leading-relaxed">
                          {currentDoc.code}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="mt-12 pt-8 border-t border-slate-800 flex items-center justify-between">
                  <Link
                    to="/"
                    className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2"
                  >
                    ← Volver al inicio
                  </Link>
                  <Link
                    to="/download"
                    className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2"
                  >
                    Descargar Blox →
                  </Link>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DocsPage;